import React, { FC } from 'react';
import { View } from 'react-native';
import { connect } from 'react-redux';

import ColorWrapper from '@styles/colors';

import { SmallLoading } from '@components/loading';

import { scale } from '@components/frame';
import { isTabletOrWeb } from '@helpers';
import { TEST_IDS } from '@v2/pages/common/constants/base.constants';

import { NCFileVersionsProps } from './types';
import LeftColumn from './components/LeftColumn';
import WideColumns from './components/WideColumns';
import NarrowColumns from './components/NarrowColumns';
// NCFileVersionDetailsModeSelectors removed from tablet/web layout (SF-4995); still used by phone details screen
import { useManageNcFileVersions } from './hooks/useManageNcFileVersions';
import { getNCFile } from '@reducers/machines';
import NCFileDiffSheet from './components/NCFileDiffSheet';

const NCFileVersions: FC<NCFileVersionsProps> = ({
  C,
  ncFile,
  loaded,
  ncFileId,
  selectedItem,
  machineId,
  shouldLoadNcFile = false,
  twoColumnView = false,
  shouldDisplayFilterInactiveVersionsButton = true,
  showOnlyActiveVersions: showOnlyActiveVersionsInitialState = true,
  getNCFile,
  enableResponsiveTwoColumnView = false,
  twoColumnBreakpoint,
  mobileBreakpoint,
  forceTwoColumnOnNativeTablet = false,
}) => {
  const {
    favorite,
    favoriteOrLatest,
    machineWhichRanFavoriteOrLatest,
    selectedVersion,
    showOnlyActiveVersions,
    setShowOnlyActiveVersions,
    otherVersions,
    onSelectVersion,
    onSendToMachine,
    onToggleFavoriteVersion,
    onMarkThisVersionAsInactive,
    // Comparison mode
    comparisonMode,
    versionsToCompare,
    showDiffModal,
    enterComparisonMode,
    exitComparisonMode,
    toggleVersionForComparison,
    startComparison,
    closeDiffModal,
    replaceVersionForComparison,
    onUploadNewVersion,
    // Layout
    isMobileWebViewport,
    computedTwoColumnView,
    inactiveVersionsCount,
    hasMultipleVersions,
  } = useManageNcFileVersions({
    ncFile,
    ncFileId,
    machineId,
    shouldLoadNcFile,
    showOnlyActiveVersionsInitialState,
    C,
    enableResponsiveTwoColumnView,
    twoColumnBreakpoint,
    mobileBreakpoint,
    forceTwoColumnOnNativeTablet,
    twoColumnView,
  });

  return (
    <View style={{ backgroundColor: C('Background/Light/Detail'), flex: 1, width: '100%', alignItems: 'center' }}>
      <View style={[{ flex: 1, width: '100%' }]}>
        {loaded && ncFile ? (
          <View
            style={{
              flex: 1,
              width: '100%',
              flexDirection: isMobileWebViewport ? 'column' : 'row',
              gap: scale(18),
            }}
          >
            {favoriteOrLatest && selectedVersion && (
              <LeftColumn
                C={C}
                favoriteOrLatest={favoriteOrLatest}
                favorite={favorite}
                selectedVersion={selectedVersion}
                onSelectVersion={onSelectVersion}
                onToggleFavoriteVersion={onToggleFavoriteVersion}
                machineWhichRanFavoriteOrLatest={machineWhichRanFavoriteOrLatest}
                otherVersions={otherVersions}
                shouldDisplayFilterInactiveVersionsButton={shouldDisplayFilterInactiveVersionsButton}
                showOnlyActiveVersions={showOnlyActiveVersions}
                setShowOnlyActiveVersions={setShowOnlyActiveVersions}
                inactiveVersionsCount={inactiveVersionsCount}
                comparisonMode={comparisonMode}
                versionsToCompare={versionsToCompare}
                onExitComparisonMode={exitComparisonMode}
                onToggleVersionForComparison={toggleVersionForComparison}
                onStartComparison={startComparison}
                forceFullWidth={isMobileWebViewport}
                onUploadNewVersion={onUploadNewVersion}
                showCompareCardInline={computedTwoColumnView && hasMultipleVersions}
                onEnterComparisonMode={enterComparisonMode}
              />
            )}
            {isTabletOrWeb && !isMobileWebViewport && (
              <>
                {computedTwoColumnView ? (
                  <NarrowColumns
                    C={C}
                    ncFile={ncFile}
                    selectedItem={selectedItem}
                    selectedVersion={selectedVersion}
                    onSendToMachine={onSendToMachine}
                  />
                ) : (
                  <WideColumns
                    C={C}
                    ncFile={ncFile}
                    selectedItem={selectedItem}
                    selectedVersion={selectedVersion}
                    favoriteOrLatest={favoriteOrLatest}
                    hasMultipleVersions={hasMultipleVersions}
                    onSendToMachine={onSendToMachine}
                    onMarkThisVersionAsInactive={onMarkThisVersionAsInactive}
                    comparisonMode={comparisonMode}
                    onEnterComparisonMode={enterComparisonMode}
                    versionsToCompare={versionsToCompare}
                    onExitComparisonMode={exitComparisonMode}
                    onStartComparison={startComparison}
                  />
                )}
              </>
            )}
          </View>
        ) : (
          <View testID={TEST_IDS.ncFileVersions.loading} style={{ marginTop: scale(100) }}>
            <SmallLoading />
          </View>
        )}
      </View>

      <NCFileDiffSheet
        C={C}
        visible={isTabletOrWeb && showDiffModal && versionsToCompare.length === 2 && Boolean(getNCFile)}
        oldVersionData={versionsToCompare[0]}
        newVersionData={versionsToCompare[1]}
        getNCFile={getNCFile}
        availableVersions={ncFile?.versions ?? []}
        onReplaceVersion={replaceVersionForComparison}
        onClose={closeDiffModal}
        ncFileName={ncFile?.ncFileName}
        onToggleFavoriteVersion={
          onToggleFavoriteVersion
            ? (version) => {
                const target = ncFile?.versions?.find((v) => v.ncFileVersionId === version.ncFileVersionId);
                if (target) {
                  onToggleFavoriteVersion(target);
                }
              }
            : undefined
        }
      />
    </View>
  );
};

export default connect(({ machines: { selectedItem } }) => ({ selectedItem }), { getNCFile })(ColorWrapper(NCFileVersions));
